import React, { ReactNode } from 'react';
import moment from 'moment';
import { Box, Typography } from '@mui/material';
import { padStart } from 'lodash';
import { BrandProject, IProjectFiles } from '../constants';

export interface IComment {
  id?: string;
  message: string;
  timestamp?: number;
  timestamp_end?: number;
  created_at?: string;
  // Null for comments a guest left on a public share page (see guest_name).
  user: {
    userid: number;
    displayName?: string;
    firstName?: string;
    emailAddress?: string;
  } | null;
  guest_name?: string | null;
  // Set by the thread fetch: a brand user's or share-link guest's comment,
  // which internal staff may delete.
  client_comment?: boolean;
  edited?: boolean;
  deleted?: boolean;
  s3_file_id?: number;
  // "Apply to other videos" copies point at the original comment (the group
  // root); null on originals and on copies whose root was hard-deleted.
  source_message_id?: number | null;
  // Every video this comment's group sits on, one entry per deliverable at
  // its current version. The API sends it only when the group spans 2+.
  applied_to?: IAppliedVideo[];
}

/** One video a comment was applied to, and the group member left on it. */
export interface IAppliedVideo {
  comment_id: number;
  file: IProjectFiles;
}

export interface ConfirmationProps {
  title: string;
  description?: string;
  children?: ReactNode;
  confirmLabel: string;
  onConfirm: () => Promise<void>;
  confirmBtnColor?: string;
}

export const DeleteDialogProps: ConfirmationProps = {
  title: 'Delete Video',
  confirmLabel: 'Delete video',
  description:
    'Are you sure you want to delete this video? You will be able to upload the video again.',
  confirmBtnColor: 'red',
  onConfirm: async () => {},
};

export const ShareDialogProps: ConfirmationProps = {
  title: 'Share Video',
  confirmLabel: 'Approve',
  description: 'Are you sure you are ready to approve this video?',
  onConfirm: async () => {},
};

export const ApproveDialogProps: ConfirmationProps = {
  title: 'Approve Video',
  confirmLabel: 'Approve',
  description: 'Are you sure you want to approve this video?',
  onConfirm: async () => {},
};

/**
 * Shown in place of the comment box (and as the header banner) once feedback
 * has been submitted and the project sits in Editing. The video stays
 * playable; commenting reopens when post delivers the next version.
 */
export const REVIEW_LOCKED_TITLE = 'Feedback submitted — video is in editing';
export const REVIEW_LOCKED_MESSAGE =
  'Feedback has already been submitted for this video and our team is working on the edits. You can still watch and review the current version, but commenting is paused until the new version is delivered.';

export const PREVIOUS_VERSION_TITLE = 'Previous version (view only)';
export const PREVIOUS_VERSION_MESSAGE =
  'Kept for reference, with the comments left on it at the time. Commenting, edit requests, approval and downloads all happen on the current version.';

/** Friendly copy for the API's 409 FeedbackAlreadySubmitted. */
export const REVIEW_LOCKED_ERROR = 'Feedback has already been submitted and this video is in editing. Commenting will reopen when the new version is delivered.';

/**
 * The API's 400 InvalidTargetFiles for "Apply to other videos": a picked
 * video was replaced by a new version or removed after the picker loaded. The
 * picker matches on this message to refetch its list.
 */
export const COPY_TARGETS_STALE_ERROR = 'Some videos changed since this list loaded. Refresh the list and try again.';

export const RequestEditDialogProps: ConfirmationProps = {
  title: 'Submit Feedback',
  confirmLabel: 'Submit',
  description:
    'Please make sure all comments, changes, and edit requests have been placed in the chat before submitting feedback. Commenting will not be available, once you submit, while editing is in progress',
  onConfirm: async () => {},
};

export const SubmitFeedbackDialogProps = (comments = 0): ConfirmationProps => ({
  title: 'Submit Feedback',
  confirmLabel: 'Submit',
  children: (
    <Box className='flex flex-col gap-1'>
      <Typography variant='body2'>
        Are you sure you want to share this video with the client
      </Typography>
      {comments && (
        <Typography variant='body2'>
          You have added{' '}
          <span className='text-accent-text'>{comments} comments</span> to the
          video
        </Typography>
      )}
    </Box>
  ),
  onConfirm: async () => {},
});

export interface IVideoFeedbackProps {
  revisions?: number;
  disableCommentMessage?: string;
  project: BrandProject;
  video?: any; // This should be a more specific type based on your video object structure
  showFeedbackCriteria?: boolean;
  /**
   * Brand file-type allowlist for the header's Download button, when the
   * caller has already resolved it (public order pages hold the brand on the
   * order). `undefined` lets DownloadBtn derive it from project.brands.
   */
  downloadFileTypes?: Set<string> | null;
  onEditAsComplete?: (videoId: string) => void;
  onFeedbackSubmit?: (videoId: string, comments: IComment[]) => void;
  onApproved?: () => void;
}

export const formatVideoUrl = (s: string) => {
  const ext = `.${s?.substring(s.lastIndexOf('.') + 1)}`;
  return s?.replace(ext, `_optimized${ext}`);
};

export const toMmSs = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${padStart(String(mins), 2, '0')}:${padStart(String(secs), 2, '0')}`;
};

export const toSeconds = (timeString: string) => {
  const time = timeString.split(':').map((t) => Number(t.trim()));
  return time[0] * 60 + time[1];
};

export const dummyVideoUrl = {
  id: 100,
  type: 'video/mp4',
  s3Location:
    'http://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
};

export const formatDate = (date?: string, showTime = true) =>
  moment(date).format(`MMM DD, yyyy ${showTime ? 'LT' : ''}`);

export const CriteriaFields = {
  editable: [
    'Removing or reordering B-Roll',
    'Replacing* talking points or B-Roll',
    'Zooming in or out of a frame',
  ],
  nonEditable: [
    'Adding talking points not listed on the Intake Form',
    'Changing an individual word within a sentence',
    'Altering pace of Expert’s voice',
    'Changing background visuals',
    'Changing Expert appearance',
  ],
};
